import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 155 · MFA Enforcement — a role-required user whose backend
// login/register/google-exchange returned ``mfa_setup_required: true``
// holds a scoped ``mfa_setup_only`` JWT that unlocks ONLY the
// ``/api/auth/mfa/*`` endpoints. We stash it under a distinct key so
// the normal ``token`` slot stays reserved for full-session tokens
// (and the /auth/me init call never sees the scoped token).
const MFA_SETUP_TOKEN_KEY = 'mfa_setup_token';
const MFA_SETUP_USER_KEY = 'mfa_setup_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [mfaSetupToken, setMfaSetupToken] = useState(
    localStorage.getItem(MFA_SETUP_TOKEN_KEY),
  );
  const [mfaSetupUser, setMfaSetupUser] = useState(() => {
    try {
      const raw = localStorage.getItem(MFA_SETUP_USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Iter 153 · Emergent Auth playbook §4 race-condition guard.
      // When the user returns from a Google OAuth flow, the URL hash
      // contains ``#session_id=…``. If we call ``/auth/me`` here
      // BEFORE ``AuthCallback`` has exchanged that session_id and
      // written the new token to localStorage, we either 401 with a
      // stale token or worse — clobber the fresh session mid-race.
      // Skip auth init on that path; ``AuthCallback`` will call
      // ``setAuthFromToken`` once it has the CaraLoom JWT in hand.
      if (typeof window !== 'undefined'
          && window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const _stashSetup = (setupToken, setupUser) => {
    localStorage.setItem(MFA_SETUP_TOKEN_KEY, setupToken);
    localStorage.setItem(MFA_SETUP_USER_KEY, JSON.stringify(setupUser || {}));
    setMfaSetupToken(setupToken);
    setMfaSetupUser(setupUser || null);
  };

  const _clearSetup = () => {
    localStorage.removeItem(MFA_SETUP_TOKEN_KEY);
    localStorage.removeItem(MFA_SETUP_USER_KEY);
    setMfaSetupToken(null);
    setMfaSetupUser(null);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data?.mfa_required) {
      return { mfaRequired: true, mfaToken: response.data.mfa_token, email: response.data.email };
    }
    if (response.data?.mfa_setup_required) {
      _stashSetup(response.data.token, response.data.user);
      return { mfaSetupRequired: true, user: response.data.user };
    }
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const verifyMfaLogin = async (mfaToken, code) => {
    const response = await axios.post(`${API}/auth/mfa/verify-login`, { mfa_token: mfaToken, code });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    if (response.data?.mfa_setup_required) {
      _stashSetup(response.data.token, response.data.user);
      return { mfaSetupRequired: true, user: response.data.user };
    }
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = async () => {
    // Best-effort server-side logout to revoke the refresh cookie.
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    _clearSetup();
  };

  // Iter 155 · Called from ``AuthCallback`` when the backend
  // returns ``mfa_setup_required`` for a fresh Google sign-in.
  const setMfaSetupFromResponse = (setupToken, setupUser) => {
    _stashSetup(setupToken, setupUser);
  };

  // Iter 155 · Called after ``/api/auth/mfa/enable`` succeeds.
  // The backend upgrades the scoped setup token to a full session
  // token when the caller was on ``mfa_setup_only`` — swap it in and
  // clear the scoped one so normal routing resumes.
  const completeMfaSetup = ({ token: fullToken, user: userData }) => {
    if (!fullToken || !userData) return;
    localStorage.setItem('token', fullToken);
    setToken(fullToken);
    setUser(userData);
    _clearSetup();
  };

  const setAuthFromToken = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    _clearSetup();
    return userData;
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const getMfaSetupAuthHeader = () => ({
    headers: { Authorization: `Bearer ${mfaSetupToken}` }
  });

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout,
      getAuthHeader, setUser, setAuthFromToken, verifyMfaLogin,
      // Iter 155 · MFA setup gating
      mfaSetupToken, mfaSetupUser, setMfaSetupFromResponse,
      completeMfaSetup, getMfaSetupAuthHeader,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
